import { getBrowserFingerprint, detectIncognito } from '../browserFingerprint';

// Make detectIncognito available globally for the incognito check
window.detectIncognito = detectIncognito;

class FreeTrialManager {
  constructor() {
    this.STORAGE_KEY = 'trial_status';
    this.lastAttemptTime = 0;
    this.cachedStatus = null;
    this.retryDelay = 30000; // 30 seconds between retries
    this.isLocalDevelopment = window.location.hostname === 'localhost';
  }

  async getUserStatus(forceRefresh = false) {
    try {
      // Check if we have a cached status and if we're within the retry delay period
      const now = Date.now();
      if (!forceRefresh && this.cachedStatus && (now - this.lastAttemptTime < this.retryDelay)) {
        console.log('[DEBUG-TRIAL] Using cached status:', this.cachedStatus);
        return this.cachedStatus;
      }
      
      // Update last attempt time
      this.lastAttemptTime = now;
      
      // Check if a test has been taken in this session
      const testTakenToday = sessionStorage.getItem('test_taken_today') === 'true';
      if (testTakenToday) {
        console.log('[DEBUG-TRIAL] Test already taken in this session - SESSION STORAGE ONLY');
        this.cachedStatus = {
          type: 'trial',
          testsAvailable: 0,
          nextTestTime: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          sessionLimited: true
        };
        return this.cachedStatus;
      }
      
      // Check if we're in incognito mode
      try {
        // Multiple incognito detection methods for better accuracy
        let isIncognito = false;
        let incognitoDetectionMethods = [];
        
        // Method 1: Use the detectIncognito function from browserFingerprint.js
        if (window.detectIncognito) {
          try {
            const result = await window.detectIncognito();
            isIncognito = isIncognito || result;
            incognitoDetectionMethods.push({ method: 'detectIncognito', result });
          } catch (e) {
            console.error('[DEBUG-TRIAL] Error in detectIncognito method:', e);
          }
        }
        
        // Method 2: Check localStorage availability
        try {
          localStorage.setItem('incognito_test', '1');
          localStorage.removeItem('incognito_test');
          incognitoDetectionMethods.push({ method: 'localStorage', result: false });
        } catch (e) {
          isIncognito = true;
          incognitoDetectionMethods.push({ method: 'localStorage', result: true, error: e.message });
        }
        
        // Method 3: Check if cookies are enabled but sessionStorage is not
        try {
          const cookiesEnabled = navigator.cookieEnabled;
          let sessionStorageEnabled = true;
          
          try {
            sessionStorage.setItem('incognito_test', '1');
            sessionStorage.removeItem('incognito_test');
          } catch (e) {
            sessionStorageEnabled = false;
          }
          
          if (cookiesEnabled && !sessionStorageEnabled) {
            isIncognito = true;
          }
          
          incognitoDetectionMethods.push({
            method: 'cookiesVsSessionStorage',
            result: cookiesEnabled && !sessionStorageEnabled,
            cookiesEnabled,
            sessionStorageEnabled
          });
        } catch (e) {
          console.error('[DEBUG-TRIAL] Error in cookies vs sessionStorage check:', e);
        }
        
        // Method 4: Check for previous fingerprint cookie vs current fingerprint
        const fpCookie = document.cookie.split(';').find(c => c.trim().startsWith('fp_id='));
        const storedFingerprint = fpCookie ? fpCookie.split('=')[1] : null;
        const currentFingerprint = await getBrowserFingerprint();
        
        if (storedFingerprint && currentFingerprint && storedFingerprint !== currentFingerprint) {
          console.log('[DEBUG-TRIAL] Fingerprint mismatch detected - possible incognito or cleared cookies');
          isIncognito = true;
          incognitoDetectionMethods.push({
            method: 'fingerprintMismatch',
            result: true,
            storedFingerprintLength: storedFingerprint ? storedFingerprint.length : 0,
            currentFingerprintLength: currentFingerprint ? currentFingerprint.length : 0
          });
        } else {
          incognitoDetectionMethods.push({
            method: 'fingerprintMismatch',
            result: false
          });
        }
        
        console.log('[DEBUG-TRIAL] Incognito detection results:', {
          isIncognito,
          methods: incognitoDetectionMethods
        });
        
        if (isIncognito) {
          console.log('[DEBUG-TRIAL] Incognito mode detected, checking IP-based trial status');
          // In incognito mode, we need to be more strict about trial access
          // Force a server check to see if this IP has already used a trial
          const fingerprint = await getBrowserFingerprint();
          console.log('[DEBUG-TRIAL] Incognito fingerprint:', fingerprint ? (fingerprint.substring(0, 10) + '...') : 'none');
          
          const headers = {
            'Content-Type': 'application/json'
          };
          
          if (fingerprint && typeof fingerprint === 'string' && fingerprint.length > 10) {
            headers['X-Browser-Fingerprint'] = fingerprint;
            headers['X-Incognito-Mode'] = 'true';
            console.log('[DEBUG-TRIAL] Including incognito flag in headers');
          } else {
            console.log('[DEBUG-TRIAL] No valid fingerprint for incognito request');
          }
          
          const response = await fetch('/api/trial/register', {
            method: 'POST',
            headers: headers,
            credentials: 'same-origin'
          });
          
          if (!response.ok) {
            throw new Error('Error checking trial status in incognito mode');
          }
          
          const data = await response.json();
          
          if (data.success && data.trial.testsAvailable === 0) {
            console.log('IP has already used trial access today');
            this.cachedStatus = {
              type: 'trial',
              testsAvailable: 0,
              nextTestTime: new Date(data.trial.expiresAt).toISOString(),
              ipLimited: true
            };
            return this.cachedStatus;
          }
        }
      } catch (e) {
        console.error('Error checking incognito mode:', e);
      }
      
      // For local development without API, provide a fallback
      if (this.isLocalDevelopment) {
        // Check localStorage for a simulated trial
        const storedTrial = localStorage.getItem(this.STORAGE_KEY);
        if (storedTrial) {
          const trialData = JSON.parse(storedTrial);
          const expiresAt = new Date(trialData.expiresAt);
          const currentTime = new Date();
          
          // Calculate tests available (1 per day)
          const testsAvailable = expiresAt > currentTime ? 1 : 0;
          
          this.cachedStatus = {
            type: 'trial',
            testsAvailable,
            nextTestTime: testsAvailable === 0 ? expiresAt.toISOString() : null,
            isLocalSimulation: true
          };
          
          return this.cachedStatus;
        }
        
        // Create a new simulated trial
        const expiresAt = new Date();
        expiresAt.setHours(expiresAt.getHours() + 24); // 24 hour trial
        
        const trialData = {
          expiresAt: expiresAt.toISOString()
        };
        
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(trialData));
        
        this.cachedStatus = {
          type: 'trial',
          testsAvailable: 1,
          nextTestTime: null,
          isLocalSimulation: true
        };
        
        return this.cachedStatus;
      }
      
      // Get browser fingerprint
      const fingerprint = await getBrowserFingerprint();

      // Try to get trial access
      console.log('Registering for trial with fingerprint:', fingerprint ? (fingerprint.substring(0, 10) + '...') : 'null or undefined');
      
      // Create a unique session identifier that persists across page refreshes but not across browsers
      // This helps identify the same user even if they clear cookies or use incognito mode
      const sessionId = localStorage.getItem('session_id') ||
                       sessionStorage.getItem('session_id') ||
                       document.cookie.split(';').find(c => c.trim().startsWith('session_id='))?.split('=')[1] ||
                       Math.random().toString(36).substring(2, 15) +
                       Math.random().toString(36).substring(2, 15);
      
      // Store the session ID in multiple locations
      try {
        localStorage.setItem('session_id', sessionId);
        sessionStorage.setItem('session_id', sessionId);
        document.cookie = `session_id=${sessionId}; path=/; max-age=31536000; SameSite=Strict`;
        console.log('Using persistent session ID for tracking:', sessionId.substring(0, 8) + '...');
      } catch (e) {
        console.error('Error storing session ID:', e);
      }
      
      // Prepare headers
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Only include fingerprint if it's valid
      if (fingerprint && typeof fingerprint === 'string' && fingerprint.length > 10) {
        headers['X-Browser-Fingerprint'] = fingerprint;
        console.log('Including valid fingerprint in request');
      } else {
        console.log('No valid fingerprint available for request');
      }
      
      // Include session ID for tracking
      headers['X-Session-ID'] = sessionId;
      console.log('Including session ID in request headers:', sessionId.substring(0, 8) + '...');
      
      const response = await fetch('/api/trial/register', {
        method: 'POST',
        headers: headers,
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error('Error checking trial status');
      }

      const data = await response.json();
      
      if (data.success) {
        const expiresAt = new Date(data.trial.expiresAt);
        const now = new Date();
        
        // Calculate tests available (1 per day)
        const testsAvailable = expiresAt > now ? 1 : 0;
        
        this.cachedStatus = {
          type: 'trial',
          testsAvailable,
          nextTestTime: testsAvailable === 0 ? expiresAt.toISOString() : null
        };
        
        return this.cachedStatus;
      }

      this.cachedStatus = {
        type: 'trial',
        testsAvailable: 0,
        nextTestTime: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // Next day
      };
      
      return this.cachedStatus;

    } catch (error) {
      console.error('Error getting trial status:', error);
      
      // Cache the error status to prevent continuous retries
      this.cachedStatus = {
        type: 'trial',
        testsAvailable: 0,
        error: error.message
      };
      
      return this.cachedStatus;
    }
  }

  async recordTestTaken() {
    // Get current status with force refresh to ensure it's up-to-date
    const status = await this.getUserStatus(true);
    
    // If we're in local development mode, update the local storage
    if (this.isLocalDevelopment && status.isLocalSimulation) {
      // Set expiration to now (used up the test)
      const trialData = {
        expiresAt: new Date().toISOString(),
        lastTestDate: new Date().toISOString()
      };
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(trialData));
      
      // Update cached status
      this.cachedStatus = {
        type: 'trial',
        testsAvailable: 0,
        nextTestTime: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        isLocalSimulation: true
      };
      
      // Also store a flag in sessionStorage to prevent multiple tests in the same session
      sessionStorage.setItem('test_taken_today', 'true');
    } else {
      // For production, call the API to record the test taken
      try {
        const fingerprint = await getBrowserFingerprint();
        
        console.log('Recording test taken with fingerprint:', fingerprint ? (fingerprint.substring(0, 10) + '...') : 'null or undefined');
        
        // Get the session ID that was created during getUserStatus
        const sessionId = localStorage.getItem('session_id') ||
                         sessionStorage.getItem('session_id') ||
                         document.cookie.split(';').find(c => c.trim().startsWith('session_id='))?.split('=')[1] ||
                         Math.random().toString(36).substring(2, 15) +
                         Math.random().toString(36).substring(2, 15);
        
        console.log('Using session ID for test taken recording:', sessionId.substring(0, 8) + '...');
        
        // Prepare headers
        const headers = {
          'Content-Type': 'application/json',
          'X-Test-Taken': 'true'
        };
        
        // Only include fingerprint if it's valid
        if (fingerprint && typeof fingerprint === 'string' && fingerprint.length > 10) {
          headers['X-Browser-Fingerprint'] = fingerprint;
          console.log('Including valid fingerprint in test taken request');
        } else {
          console.log('No valid fingerprint available for test taken request');
        }
        
        // Include session ID for tracking
        headers['X-Session-ID'] = sessionId;
        console.log('Including session ID in test taken request headers:', sessionId.substring(0, 8) + '...');
        
        console.log('Sending test taken request with details:', {
          fingerprint: fingerprint ? (fingerprint.substring(0, 10) + '...') : 'none',
          sessionId: sessionId ? (sessionId.substring(0, 8) + '...') : 'none',
          headers: Object.keys(headers).reduce((obj, key) => {
            obj[key] = key === 'X-Browser-Fingerprint' ?
              (headers[key].substring(0, 10) + '...') : headers[key];
            return obj;
          }, {})
        });
        
        const response = await fetch('/api/trial/register', {
          method: 'POST',
          headers: headers,
          credentials: 'same-origin'
        });
        
        if (!response.ok) {
          console.error('Failed to record test taken:', response.statusText);
        } else {
          try {
            const responseData = await response.json();
            console.log('Test taken recording response:', responseData);
          } catch (e) {
            console.error('Error parsing test taken response:', e);
          }
        }
        
        // Update cached status to reflect test taken
        if (this.cachedStatus) {
          this.cachedStatus.testsAvailable = 0;
          this.cachedStatus.nextTestTime = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
        }
        
        // Also store a flag in sessionStorage to prevent multiple tests in the same session
        sessionStorage.setItem('test_taken_today', 'true');
      } catch (error) {
        console.error('Error recording test taken:', error);
      }
    }
    
    // Force invalidate the cache to ensure next status check gets fresh data
    this.lastAttemptTime = 0;
    this.cachedStatus = null;
    
    return status.testsAvailable > 0;
  }

  formatTimeUntilNextTest(nextTestTime) {
    if (!nextTestTime) return 'No disponible';
    
    const now = new Date();
    const next = new Date(nextTestTime);
    const diff = next - now;
    
    if (diff <= 0) return 'Disponible ahora';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }
}

export default new FreeTrialManager();